import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logoImage from "../assets/images/logoNew.jpg";
import useMediaQuery from "../Utility/MediaQuery/useMediaQuery";
import { selectSidebarState, setSidebarState, toggleSidebar } from "../Redux/Slices/sidebarSlice";
import { menuItems } from "./menuConfig";
import { ROUTES } from "../constants";
import { isSharedWorkItemDetailLocation } from "../utils/workItemDetailUrl";
import { getWorkItemsListPathForPermissions } from "../Pages/Issues/issuesTabPermissions";
import Typography from "../Utility/Typography";
import Button from "../Utility/Button/Button";
import "./sideNav.css";
import MoreSpacesPopup from "../Components/MoreSpacesPopup/MoreSpacesPopup";
import { usePermissions } from "../hooks/usePermissions";
import { RiArrowRightSLine, RiArrowLeftDoubleFill } from "react-icons/ri";

function passesSidebarPermission(can, perm) {
  if (perm == null) return true;
  if (Array.isArray(perm)) return perm.some((k) => can(k));
  return can(perm);
}

function filterMenuForPermissions(items, can) {
  return items
    .map((item) => {
      if (item.hasSubmenu && Array.isArray(item.submenuItems)) {
        const subs = item.submenuItems.filter((sub) =>
          passesSidebarPermission(can, sub.sidebarPermission)
        );
        if (subs.length === 0) return null;
        return { ...item, submenuItems: subs };
      }
      return passesSidebarPermission(can, item.sidebarPermission) ? item : null;
    })
    .filter(Boolean);
}

const SideNav = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const visibleMenuItems = useMemo(() => filterMenuForPermissions(menuItems, can), [can]);
  const toggleNav = useSelector(selectSidebarState);
  const isSmTab = useMediaQuery("(max-width: 768px)");
  const location = useLocation();
  const isSharedWorkItemDetailView = useMemo(
    () => isSharedWorkItemDetailLocation(location),
    [location]
  );
  const checkpathname = location.pathname.split("/")[1];
  const [expandedMenus, setExpandedMenus] = useState({});
  const [showMoreSpaces, setShowMoreSpaces] = useState(false);

  useEffect(() => {
    if (!toggleNav && isSmTab) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [toggleNav, isSmTab]);

  // Auto-expand menu if any visible submenu item is active
  useEffect(() => {
    const newExpandedMenus = {};

    visibleMenuItems.forEach((item) => {
      if (item.hasSubmenu && item.submenuItems) {
        const hasActiveSubItem = item.submenuItems.some(
          (subItem) => location.pathname === subItem.path
        );

        if (hasActiveSubItem) {
          newExpandedMenus[item.id] = true;
        }
      }
    });

    setExpandedMenus(newExpandedMenus);
  }, [location.pathname, visibleMenuItems]);

  const closeSidebar = () => {
    if (isSmTab) {
      dispatch(setSidebarState(true)); // true = hidden on mobile
    }
  };

  const handleMenuToggle = () => {
    dispatch(toggleSidebar());
  };

  const isActive = (path) => {
    if (isSharedWorkItemDetailView) return false;
    return checkpathname === path || (!checkpathname && path === "dashboard");
  };

  /** Match submenu by full path only — avoid treating every `/settings/...` route as Settings. */
  const isSubMenuActive = (submenuItems) => {
    if (!submenuItems) return false;
    return submenuItems.some((item) => location.pathname === item.path);
  };
  const toggleMenu = (menuId) => {
    setExpandedMenus((prev) => ({
      [menuId]: !prev[menuId],
    }));
  };

  const renderMenuItem = (item) => {
    const IconComponent = item.icon;
    const isItemActive =
      item.activePath === "projects" ? isActive("projects") : isActive(item.activePath);
    const isSubActive = isSubMenuActive(item.submenuItems);

    if (item.hasSubmenu) {
      return (
        <li
          key={item.id}
          className={`${!toggleNav && "group"} nav-item border-bottom overflow-hidden ${
            isSmTab ? "" : "hover:!overflow-visible"
          }`}
        >
          <div
            className={`nav-link flex items-center cursor-pointer sidebar-link ${
              isSubActive ? "sidebar-link-active" : ""
            } ${
              toggleNav
                ? "px-3 justify-center"
                : "px-3 w-[270px] group-hover:bg-[var(--sidebar-hover)] text-[var(--text-white)]"
            } py-3 hover:bg-[var(--sidebar-hover)] group menu-link ${isSubActive ? "active" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              toggleMenu(item.id);
              // Navigate to the main path if it exists
              if (item.path) {
                navigate(item.path);
              }
            }}
          >
            <IconComponent
              className={`sidebar-icon ${toggleNav ? "sidebar-icon-collapsed" : ""} ${
                isSubActive ? "sidebar-icon-active" : ""
              } group-hover:!text-[var(--text-white)] group-hover:opacity-100 inline-block items-center justify-center flex text-center leading-5 text-lg ${
                isSubActive ? "opacity-100" : "opacity-70"
              } ${toggleNav ? "" : "me-2"}`}
            />
            {!toggleNav && (
              <div className="flex items-center justify-between w-[70%]">
                <Typography
                  variant="text2"
                  component="span"
                  data-key={`t-${item.activePath}`}
                  className="sidebar-text group-hover:!text-[var(--text-white)] text-nowrap font-medium"
                >
                  {item.title}
                </Typography>
                <RiArrowRightSLine
                  className={`sidebar-text group-hover:!text-[var(--text-white)] text-nowrap font-medium text-lg transition-transform duration-300 ${
                    expandedMenus[item.id] || isSubActive
                      ? "rotate-90 opacity-100"
                      : "rotate-0 opacity-70"
                  }`}
                />
              </div>
            )}
          </div>
          {!toggleNav && (
            <div
              id={`collapse${item.id}`}
              className={`menu-dropdown transition-all duration-300 ease-in-out ${
                expandedMenus[item.id] || isSubActive
                  ? "max-h-[1000px] opacity-100"
                  : "max-h-0 opacity-0 overflow-hidden"
              }`}
            >
              <div className="p-0">
                <ul className="list-none m-0 p-0">
                  {item.submenuItems.map((subItem) => {
                    const isSubItemActive = location.pathname === subItem.path;
                    const isMoreSpaces = subItem.activePath === "more-spaces";
                    const SubIconComponent = subItem.icon;

                    return (
                      <li
                        key={subItem.id}
                        onClick={closeSidebar}
                        className="nav-item border-top hover:bg-[var(--sidebar-hover)]"
                      >
                        {isMoreSpaces ? (
                          /* ✅ MORE SPACES → ICON */
                          <div
                            onClick={() => setShowMoreSpaces(true)}
                            className="nav-link flex items-center py-3 px-8 menu-link sidebar-link cursor-pointer"
                          >
                            <SubIconComponent className="sidebar-icon opacity-70 text-lg me-2" />

                            <Typography variant="text2" component="span">
                              {subItem.title}
                            </Typography>
                          </div>
                        ) : (
                          /* ✅ NUMBERED SUBMENU */
                          <Link
                            to={subItem.path}
                            className={`nav-link flex items-center py-3 px-8 menu-link sidebar-link ${
                              isSubItemActive ? "active2 sidebar-link-active" : ""
                            }`}
                          >
                            <Typography
                              variant="text2"
                              component="span"
                              className="hover:!text-[var(--text-white)] text-nowrap text-base"
                            >
                              {subItem.title}
                            </Typography>
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </li>
      );
    }

    return (
      <li
        key={item.id}
        onClick={closeSidebar}
        className={`nav-item border-bottom hover:bg-[var(--sidebar-hover)] overflow-hidden ${
          isSmTab ? "" : "hover:!overflow-visible"
        }`}
      >
        <Link
          to={
            item.path === ROUTES.ISSUES || item.activePath === "issues"
              ? getWorkItemsListPathForPermissions(can)
              : item.path
          }
          className={`nav-link flex items-center text-base font-medium sidebar-link ${
            isItemActive ? "sidebar-link-active" : ""
          } ${
            toggleNav ? "px-3 justify-center" : "px-3 w-[270px]"
          } py-3 hover:bg-[var(--sidebar-hover)] group menu-link ${isItemActive ? "active" : ""}`}
        >
          <IconComponent
            className={`sidebar-icon ${toggleNav ? "sidebar-icon-collapsed" : ""} ${
              isItemActive ? "sidebar-icon-active" : ""
            } group-hover:!text-[var(--text-white)] group-hover:opacity-100 inline-block items-center justify-center flex text-center leading-5 text-lg ${
              isItemActive ? "opacity-100" : "opacity-70"
            } ${toggleNav ? "" : "me-2"}`}
          />
          {!toggleNav && (
            <Typography
              variant="text2"
              component="span"
              data-key={`t-${item.activePath}`}
              className="sidebar-text group-hover:!text-[var(--text-white)] text-nowrap font-medium text-base"
            >
              {item.title}
            </Typography>
          )}
        </Link>
      </li>
    );
  };

  return (
    <>
      {/* Sidebar */}
      <div
        className={`
          app-menu navbar-menu bg-surface-primary scrollbar-hide
          fixed top-0 left-0 h-full
          transition-all duration-300 ease-in-out
          ${
            isSmTab
              ? toggleNav
                ? "-translate-x-full z-40"
                : "translate-x-0 w-64 z-50"
              : toggleNav
                ? "w-20 z-40"
                : "w-64 z-40"
          }
        `}
      >
        {/* Logo Section - Brand Box */}
        <div className="navbar-brand-box relative flex items-center justify-center">
          <Link
            to="/dashboard"
            className="flex items-start justify-start mx-[5px] my-[0px] logo logo-light"
            onClick={(e) => {
              if (toggleNav) {
                e.preventDefault();
                handleMenuToggle();
              } else {
                closeSidebar();
              }
            }}
          >
            {toggleNav ? (
              <Typography variant="span1" className="logo-sm">
                <img src={logoImage} alt="Logo" className="h-[46px] object-contain" />
              </Typography>
            ) : (
              <Typography variant="span1" className="logo-lg relative h-[46px]">
                <img
                  src={logoImage}
                  alt="Logo"
                  className="h-[46px] w-[130px] object-contain me-7"
                />
              </Typography>
            )}
          </Link>
          {!toggleNav && (
            <Button
              type="button"
              onClick={handleMenuToggle}
              className="!absolute right-3 top-1/2 -translate-y-1/2 
               !flex !items-center !justify-center !p-1
               hover:!opacity-70 !transition-opacity 
               !bg-transparent !border-0 !shadow-none !font-normal 
               !rounded-none focus:!ring-0 focus:!ring-offset-0"
              aria-label="Toggle menu"
            >
              <RiArrowLeftDoubleFill className="text-xl text-gray-400" />
            </Button>
          )}
        </div>

        {/* Scrollable Menu Area */}
        <div
          id="scrollbar"
          className="h-[calc(100vh-100px)] overflow-y-auto overflow-x-hidden scrollbar-hide"
        >
          <div className="">
            <ul className="navbar-nav" id="navbar-nav">
              {visibleMenuItems.map((item) => renderMenuItem(item))}
            </ul>
          </div>
        </div>
      </div>

      <MoreSpacesPopup open={showMoreSpaces} onClose={() => setShowMoreSpaces(false)} />
    </>
  );
};

export default SideNav;
